/**
 * showdownOther — the reusable Showdown animation TEMPLATES (a port of the
 * load-bearing slice of Pokémon Showdown's `BattleOtherAnims`).
 *
 * In Showdown most move scripts are not bespoke: they just point at one of a
 * handful of shared templates (`contactattack`, `attack`, `bite`, …). These are
 * the building blocks the per-move scripts in `showdownMoves.ts` compose. The
 * bodies are ported near-verbatim from `battle-animations.ts` (MIT/CC0); our
 * `FxScene` + `Actor` match Showdown's `scene` + `Sprite` API one-to-one, so the
 * sequences of `scene.showEffect(...)` / `attacker.anim(...)` / `scene.wait(...)`
 * carry over unchanged.
 *
 * IMPORTANT (test/runtime separation): this module imports ONLY TYPES from
 * `showdownFx.ts`, never values — that file pulls in Phaser, which cannot load in
 * the node test environment. Because every template is a pure function over the
 * `FxScene`/`Actor` interfaces, the type-only import keeps this file (and
 * `showdownMoves.ts`, which composes it) importable in plain node for unit tests.
 *
 * Effect-name discipline: only effect keys present in our vendored fx catalog
 * (client/data/generated/fx/catalog.json) are used. Where Showdown referenced an
 * effect we did not vendor (e.g. it drew the attacker's own sprite as a streak via
 * `attacker.sp`), the call is adapted to the nearest vendored effect or omitted —
 * noted inline at each site.
 */
import type { FxScene, Actor, MoveAnim } from './showdownFx.js';

/* ──────────────────────────────────────────────────────────────────────────
 * attack — a basic special "wisp travels to the defender" (PS `attack`).
 * ──────────────────────────────────────────────────────────────────────── */
export const attack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 1 },
    { x: defender.x, y: defender.y, z: defender.behind(40), scale: 1, opacity: 0.5 },
    'linear',
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * contactattack — the canonical physical "lunge in, hit, bounce back" (PS
 * `contactattack`). The default for most contact moves.
 * ──────────────────────────────────────────────────────────────────────── */
export const contactattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: defender.x, y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.x, y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
  scene.wait(500);
};

/* ──────────────────────────────────────────────────────────────────────────
 * slashattack — contact lunge with a slash mark left on the defender (PS
 * `slashattack`).
 * ──────────────────────────────────────────────────────────────────────── */
export const slashattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: defender.x, y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.x, y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');

  scene.showEffect(
    'rightslash',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { scale: 3, opacity: 0, time: 800 },
    'linear',
    'fade',
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * punchattack — a hard straight punch: wisps + a fist on the defender, attacker
 * jabs in and out (PS `punchattack`). Base for the elemental punches.
 * ──────────────────────────────────────────────────────────────────────── */
export const punchattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 500 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  scene.showEffect(
    'fist',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 2,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  attacker.anim(
    { x: defender.leftof(20), y: defender.y, z: defender.behind(-20), time: 400 },
    'ballistic2Under',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, time: 50 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.delay(425);
  defender.anim(
    { x: defender.leftof(-15), y: defender.y, z: defender.behind(15), time: 50 },
    'swing',
  );
  defender.anim({ time: 300 }, 'swing');
};

/* ──────────────────────────────────────────────────────────────────────────
 * bite — the two jaws snapping shut on the defender (PS `bite`). Pairs with a
 * contactattack for biting contact moves.
 * ──────────────────────────────────────────────────────────────────────── */
export const bite: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'topbite',
    { x: defender.x, y: defender.y + 50, z: defender.z, scale: 0.5, opacity: 0, time: 370 },
    { x: defender.x, y: defender.y + 10, z: defender.z, scale: 0.5, opacity: 1, time: 500 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'bottombite',
    { x: defender.x, y: defender.y - 50, z: defender.z, scale: 0.5, opacity: 0, time: 370 },
    { x: defender.x, y: defender.y - 10, z: defender.z, scale: 0.5, opacity: 1, time: 500 },
    'linear',
    'fade',
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * fastanimattack — a tiny, snappy contact bump used as the fast-forward fallback
 * for physical moves (PS `fastanimattack`). No effects; just the two lunges.
 * ──────────────────────────────────────────────────────────────────────── */
export const fastanimattack: MoveAnim = (_scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim({ z: attacker.behind(-70), time: 50, opacity: 1 }, 'decel');
  attacker.anim({ opacity: 1, time: 150 }, 'accel');
  defender.anim({ z: defender.behind(30), time: 70 }, 'decel');
  defender.anim({ time: 170 }, 'accel');
};

/* ──────────────────────────────────────────────────────────────────────────
 * fastanimspecial — a quick shadowball lob, the fast-forward fallback for special
 * moves (PS `fastanimspecial`). `shadowball` is vendored, so this is verbatim.
 * ──────────────────────────────────────────────────────────────────────── */
export const fastanimspecial: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.8, time: 0 },
    { x: defender.x, y: defender.y, z: defender.z, time: 100 },
    'decel',
  );
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.8, time: 70 },
    { x: defender.x, y: defender.y, z: defender.z, time: 170 },
    'decel',
  );
  defender.anim({ z: defender.behind(30), time: 70 }, 'decel');
  defender.anim({ time: 170 }, 'accel');
};

/* ──────────────────────────────────────────────────────────────────────────
 * fastanimself — a quick self-pulse, the fast-forward fallback for status/self
 * moves (PS `fastanimself`). Just a scale-bump on the user.
 * ──────────────────────────────────────────────────────────────────────── */
export const fastanimself: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ scale: 1.5, time: 50 }, 'decel');
  attacker.anim({ time: 170 }, 'accel');
};

/* ──────────────────────────────────────────────────────────────────────────
 * fastattack — a quick-attack-style dash (PS `fastattack`). Showdown drew the
 * attacker's own sprite (`attacker.sp`) as fading streaks; our Actor has no such
 * effect, so those two `showEffect(attacker.sp, …)` calls are omitted and the
 * blur is conveyed by the wisp puffs + the attacker's own fade-through lunge.
 * ──────────────────────────────────────────────────────────────────────── */
export const fastattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 260 },
    { scale: 2, opacity: 0, time: 560 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 310 },
    { scale: 2, opacity: 0, time: 610 },
    'linear',
  );
  // PS drew two attacker-sprite streaks here (showEffect(attacker.sp, …)); omitted
  // (no sprite-as-effect in our engine) — the attacker's fade-through lunge below
  // preserves the dash read.
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(70), time: 300, opacity: 0.5 },
    'accel',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(100), opacity: 0, time: 100 },
    'linear',
  );
  attacker.anim(
    { x: attacker.x, y: attacker.y, z: attacker.behind(70), opacity: 0, time: 1 },
    'linear',
  );
  attacker.anim({ opacity: 1, time: 500 }, 'decel');
  defender.delay(260);
  defender.anim({ z: defender.behind(30), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};
